import React from 'react';

import {
  BillingCycle,
  ProductKind,
  ProductType,
  SubscriptionInfoFragment,
  SubscriptionStatus,
  SubscriptionSubstatus,
} from '$/GraphQL/schema/types';
import { formatCurrency } from '$/utils/formatCurrency';
import { logger } from '$/utils/logger';
import { getAddOn } from './AddOns/AddOns';
import { AddOn } from './AddOns/Info/AddOn';
import { AdditionalRegistrants } from './AddOns/Info/AdditionalRegistrants';
import { PriceTierInfo } from './PriceTierInfo';
import EDate from '$/shared/frontend/utils/EDate';
import { ProductInfo, WebinarsProductInfo, getProductInfo } from './ProductInfo';

export enum SwitchingType {
  MonthlyToAnnually = 'MonthlyToAnnually',
  AnnuallyToMonthly = 'AnnuallyToMonthly',
  BackToAnnual = 'BackToAnnual',
}

export class SubscriptionInfo {
  public constructor(
    public subscriptionInfoFragment: SubscriptionInfoFragment,
    private teamId: string
  ) {}

  public get billingCycle(): BillingCycle {
    return this.subscriptionInfoFragment.billingCycle;
  }

  public get billingCycleUnit(): string {
    return this.billingCycle === BillingCycle.Month ? 'month' : 'year';
  }

  public get billingCyclely(): string {
    return this.billingCycle === BillingCycle.Month ? 'monthly' : 'annual';
  }

  public get currentPeriodBillingCycle(): BillingCycle {
    return this.subscriptionInfoFragment.currentPeriodBillingCycle;
  }

  public get status(): SubscriptionStatus {
    return this.subscriptionInfoFragment.status;
  }

  public get subStatus(): SubscriptionSubstatus {
    return this.subscriptionInfoFragment.subStatus;
  }

  public get credit(): number {
    return this.subscriptionInfoFragment.credit;
  }

  public get activeWebinarsLimit(): number {
    return Math.max(this.product.quantity, this.priceTier.to);
  }

  public get activeWebinars(): number {
    return this.subscriptionInfoFragment.activeWebinars;
  }

  public get unusedWebinars(): number {
    return this.activeWebinarsLimit - this.activeWebinars;
  }

  public get amountOwed(): number {
    return this.subscriptionInfoFragment.amountOwed ?? 0;
  }

  public get endedDate(): EDate | undefined {
    return this.subscriptionInfoFragment.endedDate
      ? new EDate(this.subscriptionInfoFragment.endedDate)
      : undefined;
  }

  public get trialEndingDate(): EDate | undefined {
    return this.subscriptionInfoFragment.trialEndingDate
      ? new EDate(this.subscriptionInfoFragment.trialEndingDate)
      : undefined;
  }

  public get nextPaymentDueDate(): EDate | undefined {
    return this.subscriptionInfoFragment.nextPaymentDueDate
      ? new EDate(this.subscriptionInfoFragment.nextPaymentDueDate)
      : undefined;
  }

  public get currency(): string {
    return this.subscriptionInfoFragment.currency;
  }

  public get products(): ProductInfo[] {
    return this.subscriptionInfoFragment.products.map(getProductInfo);
  }

  public get product(): WebinarsProductInfo {
    const product = this.products.find(({ kind }) => kind === ProductKind.Webinars);
    if (!product) {
      logger.error('No eWebinar product found in subscription info', {
        team: this.teamId,
        info: this.subscriptionInfoFragment,
      });
      throw new Error('No eWebinar product found in subscription info');
    }
    return product as WebinarsProductInfo;
  }

  public get additionalRegistrant(): AdditionalRegistrants {
    const additionalRegistrant = this.products.find(
      ({ type }) => type === ProductType.AdditionalRegistrants
    );
    return additionalRegistrant as AdditionalRegistrants;
  }

  public get isLegacyTeam(): boolean {
    return this.products.some(({ type }) => type === ProductType.Webinars2019);
  }

  public get isSuspended(): boolean {
    return [
      SubscriptionSubstatus.NoSubscription,
      SubscriptionSubstatus.ChurnedTrial,
      SubscriptionSubstatus.ChurnedDelinquent,
      SubscriptionSubstatus.ChurnedVoluntary,
    ].includes(this.subStatus);
  }

  public get isInTrial(): boolean {
    return [SubscriptionSubstatus.Trialing, SubscriptionSubstatus.TrialingCanceled].includes(
      this.subStatus
    );
  }

  public get isCancelled(): boolean {
    return [SubscriptionSubstatus.ActiveCanceled, SubscriptionSubstatus.TrialingCanceled].includes(
      this.subStatus
    );
  }

  public get addOns(): AddOn[] {
    return this.subscriptionInfoFragment.products
      .filter(({ kind }) => kind === ProductKind.AddOn)
      .map(getAddOn);
  }

  public get priceTier(): PriceTierInfo {
    const currentPriceTier = this.product.currentPriceTier;
    if (!currentPriceTier) {
      logger.error(
        'No current price tier found in subscription info',
        this.subscriptionInfoFragment
      );
      throw new Error('No current price tier found in subscription info');
    }
    return new PriceTierInfo(currentPriceTier);
  }

  public get nextPriceTier(): PriceTierInfo {
    const nextPriceTier = this.product.nextPriceTier;
    if (!nextPriceTier) {
      logger.error(
        'No current price tier found in subscription info',
        this.subscriptionInfoFragment
      );
      throw new Error('No current price tier found in subscription info');
    }
    return new PriceTierInfo(nextPriceTier);
  }

  public get prices(): {
    monthlyTotal: number;
    annualTotal: number;
    annualTotalByMonth: number;
  } {
    return this.products.reduce(
      (amount, product) => {
        return {
          monthlyTotal: amount.monthlyTotal + product.prices.monthly,
          annualTotal: amount.annualTotal + product.prices.annual,
          annualTotalByMonth: amount.annualTotalByMonth + product.prices.annualByMonth,
        };
      },
      {
        monthlyTotal: 0,
        annualTotal: 0,
        annualTotalByMonth: 0,
      }
    );
  }

  public get nextPrices(): {
    monthlyTotal: number;
    annualTotal: number;
    annualTotalByMonth: number;
  } {
    return this.products.reduce(
      (amount, product) => {
        // Skip unsubscribed add-ons
        if (product.isUnsubscribed) return amount;
        return {
          monthlyTotal: amount.monthlyTotal + product.nextPrices.monthly,
          annualTotal: amount.annualTotal + product.nextPrices.annual,
          annualTotalByMonth: amount.annualTotalByMonth + product.nextPrices.annualByMonth,
        };
      },
      {
        monthlyTotal: 0,
        annualTotal: 0,
        annualTotalByMonth: 0,
      }
    );
  }

  public get nextPriceText(): string {
    return formatCurrency(
      this.billingCycle === BillingCycle.Month
        ? this.nextPrices.monthlyTotal
        : this.nextPrices.annualTotal
    );
  }

  public get saveTotal(): number {
    return this.nextPrices.monthlyTotal * 12 - this.nextPrices.annualTotal;
  }

  public get saveTotalText(): string {
    return formatCurrency(this.saveTotal);
  }

  public get switchingType(): SwitchingType {
    switch (this.billingCycle) {
      case BillingCycle.Month:
        return this.currentPeriodBillingCycle === BillingCycle.Month
          ? SwitchingType.MonthlyToAnnually
          : SwitchingType.BackToAnnual;
      case BillingCycle.Year:
        return SwitchingType.AnnuallyToMonthly;
    }
  }

  public get switchingButtonText(): React.ReactElement {
    switch (this.switchingType) {
      case SwitchingType.AnnuallyToMonthly:
        return <span>Switch to monthly</span>;
      case SwitchingType.MonthlyToAnnually:
        return <span>Switch to annual</span>;
      case SwitchingType.BackToAnnual:
        return (
          <span>
            Switch back to annual
            {!this.isInTrial && (
              <>
                <br />
                <span className='text-xs'>You will not be charged.</span>
              </>
            )}
          </span>
        );
    }
  }
}
